/**
 * マスタ(事業部・科目①②・年度設定)の共有キャッシュ。
 * 画面ごとに読み直すとFirestoreの読み取りが増えるため、ログイン後に一度だけ読む。
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  fetchAccounts,
  fetchDepartments,
  fetchFiscalSettings,
  fetchPayerLabels,
  fetchSubAccounts,
} from './masterRepo';
import { fetchCompanies, defaultCompany } from './companyRepo';
import {
  DEFAULT_COMPANY_ID,
  DEFAULT_PAYER_SELECT_LABELS,
  type Account,
  type Company,
  type Department,
  type FiscalSettings,
  type PayerLabels,
  type SubAccount,
} from '@core/lib/types';

type MastersContextValue = {
  loading: boolean;
  error: string | null;
  companies: Company[];
  /** 入力時の初期値に使う会社(既定はSUM) */
  defaultCompanyId: string;
  companyName: (id: string) => string;
  departments: Department[];
  accounts: Account[];
  subAccounts: SubAccount[];
  fiscal: FiscalSettings;
  /** 支払い者3択の表示名(固定3択・名称のみ設定タブで変更可) */
  payerLabels: PayerLabels;
  /** 経費申請で選べる科目①のみ */
  expenseAccounts: Account[];
  subAccountsOf: (accountId: string) => SubAccount[];
  departmentName: (id: string) => string;
  accountName: (id: string) => string;
  subAccountName: (id: string) => string;
  reload: () => Promise<void>;
};

const MastersContext = createContext<MastersContextValue | null>(null);

export function MastersProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [subAccounts, setSubAccounts] = useState<SubAccount[]>([]);
  const [fiscal, setFiscal] = useState<FiscalSettings>({ start_month: 4 });
  const [payerLabels, setPayerLabels] = useState<PayerLabels>(DEFAULT_PAYER_SELECT_LABELS);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [d, a, s, f, c, pl] = await Promise.all([
        fetchDepartments(),
        fetchAccounts(),
        fetchSubAccounts(),
        fetchFiscalSettings(),
        fetchCompanies().catch(() => [] as Company[]),
        fetchPayerLabels().catch(() => DEFAULT_PAYER_SELECT_LABELS),
      ]);
      setCompanies(c);
      setDepartments(d);
      setAccounts(a);
      setSubAccounts(s);
      setFiscal(f);
      setPayerLabels(pl);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const value = useMemo<MastersContextValue>(() => {
    const deptMap = new Map<string, string>(departments.map((d) => [d.id, d.name]));
    const companyMap = new Map<string, string>(companies.map((c) => [c.id, c.name]));
    const accMap = new Map(accounts.map((a) => [a.id, a.name]));
    const subMap = new Map(subAccounts.map((s) => [s.id, s.name]));
    return {
      loading,
      error,
      companies: companies.filter((c) => c.active),
      defaultCompanyId: defaultCompany(companies)?.id ?? DEFAULT_COMPANY_ID,
      companyName: (id: string) => companyMap.get(id) ?? id,
      departments,
      accounts,
      subAccounts,
      fiscal,
      payerLabels,
      expenseAccounts: accounts.filter((a) => a.active && a.expense_selectable),
      subAccountsOf: (accountId: string) =>
        subAccounts.filter((s) => s.account_id === accountId && s.active),
      departmentName: (id: string) => deptMap.get(id) ?? id,
      accountName: (id: string) => accMap.get(id) ?? id,
      subAccountName: (id: string) => subMap.get(id) ?? id,
      reload,
    };
  }, [loading, error, companies, departments, accounts, subAccounts, fiscal, payerLabels, reload]);

  return <MastersContext.Provider value={value}>{children}</MastersContext.Provider>;
}

export function useMasters(): MastersContextValue {
  const ctx = useContext(MastersContext);
  if (!ctx) throw new Error('useMasters は MastersProvider の内側で使ってください');
  return ctx;
}
